import { supabase } from './supabase';

export const READING_LOCATIONS = ['Small group location', 'Otter Creek Church'] as const;
export type ReadingLocation = (typeof READING_LOCATIONS)[number];

export interface ReadingItem {
  id: string;
  sort_order: number;
  reading_date: string | null;
  date: string;
  month: string;
  location: ReadingLocation;
  text: string;
  reading_prompt: string | null;
  is_off: boolean;
  off_reason: string | null;
  esv_url: string | null;
  podcast_url: string | null;
  podcast_label: string | null;
  video_urls: string[];
  video_labels: string[];
  additional_urls: string[];
  additional_labels: string[];
}

export type ReadingItemInput = Omit<ReadingItem, 'id'>;

export const fetchReadingSchedule = async (): Promise<ReadingItem[]> => {
  const { data, error } = await supabase
    .from('reading_schedule')
    .select('*')
    .order('reading_date', { ascending: true, nullsFirst: false })
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return (data ?? []) as ReadingItem[];
};

export const createReadingItem = async (item: ReadingItemInput): Promise<void> => {
  const { error } = await supabase.from('reading_schedule').insert([item]);
  if (error) throw error;
};

export const updateReadingItem = async (
  id: string,
  item: Partial<ReadingItemInput>
): Promise<void> => {
  const { error } = await supabase
    .from('reading_schedule')
    .update({ ...item, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
};

export const deleteReadingItem = async (id: string): Promise<void> => {
  const { error } = await supabase.from('reading_schedule').delete().eq('id', id);
  if (error) throw error;
};

export interface ReadingPageContent {
  header_title: string;
  header_description: string;
  footer_title: string;
  footer_description: string;
}

export const fetchReadingPageContent = async (): Promise<ReadingPageContent | null> => {
  const { data, error } = await supabase
    .from('reading_page_content')
    .select('header_title, header_description, footer_title, footer_description')
    .eq('id', 'reading')
    .maybeSingle();

  if (error) throw error;
  return data as ReadingPageContent | null;
};

export const updateReadingPageContent = async (
  content: ReadingPageContent
): Promise<void> => {
  const { error } = await supabase
    .from('reading_page_content')
    .upsert({ id: 'reading', ...content, updated_at: new Date().toISOString() });
  if (error) throw error;
};